import { formatISO } from 'date-fns';
import DangerousHTMLLabel, { DangerousTranslatedLink } from 'interfaces/DangerousHTMLLabel';
import Language from '../enums/Language';
import Labels from '../enums/Labels';
import Insured from '../interfaces/Insured';
import PersonalDataForm from '../interfaces/PersonalDataForm';
import { parsePhoneNumber } from '../PersonalDetails/utils';
import AppStorage from '../Storage/Storage';
import { GeneralPractitioner } from '../interfaces/HealthDeclaration';
import { AddressEnum } from '../interfaces/Address';
import enDictionary from '../locale/en';
import nlDictionary from '../locale/nl';
import deDictionary from '../locale/de';
import HealthDocFile, { HealthDocFileValidation } from '../interfaces/HealthDocFile';
import ProductType from '../enums/ProductType';
import Brand from '../enums/Brand';
import { Nationality } from '../interfaces/Nationality';

export const isBrand = (brand: Brand): boolean => {
  const storedBrand = localStorage.getItem('brand');

  return storedBrand ? storedBrand.toLowerCase() === brand.toLowerCase() : false;
};

export const translate = (key: Labels, lang: Language, params?: Array<string>): string => {
  let translated: string;
  switch (lang) {
    case Language.English:
      translated = (enDictionary)[key];
      break;
    case Language.Dutch:
      translated = (nlDictionary)[key];
      break;
    case Language.German:
      translated = (deDictionary)[key];
      break;
    default:
      translated = '';
  }
  if (params && params.length) {
    for (let i = 0; i < params.length; i += 1) {
      translated = translated.replace(`{${i}}`, params[i]);
    }
    return translated;
  }
  return translated;
};

// TODO: Rename function, the '2' is a misnomer
export const translateWithLink2 = (key: Labels, lang: Language, params?: Array<string>): DangerousTranslatedLink => (
  {
    dangerouslySetInnerHTML: {
      __html: translate(key, lang, params),
    },
  }
);

export function injectDangerousHTML(label: string): DangerousHTMLLabel {
  return {
    __html: label,
  };
}

export const isObjectEmpty = (obj: object): boolean => (
  Object.entries(obj).length === 0 && obj.constructor === Object
);

export const transformInsuredToPersonalDataFormFormat = (
  insured: Insured,
  generalPractitioner: GeneralPractitioner | undefined,
  storage: AppStorage,
): Partial<PersonalDataForm> => {
  const {
    type,
    occupation,
    isSmoker,
    initials,
    firstName,
    middleName,
    surname,
    nationality,
    socialSecurityNumber,
    phoneNumber,
    email,
    dateOfBirth,
    gender,
    address,
  } = insured;

  const isWefox = isBrand(Brand.wefox);

  const defaultCountry = isWefox ? '' : 'NL';
  const defaultAddressType = isWefox ? AddressEnum.International : AddressEnum.Dutch;

  const {
    street,
    city,
    zipCode,
    number,
    suffix,
    country = defaultCountry,
    type: addressType = defaultAddressType,
  } = address || {};

  const {
    name: gpName,
    address: {
      city: gpCity,
    },
  } = generalPractitioner || { address: {} };

  const birthDate = formatISO(new Date(dateOfBirth), { representation: 'date' });

  const {
    phonePrefix,
    phoneNumberBody,
  } = parsePhoneNumber(phoneNumber || '');

  const greeting = gender === 'MALE' ? translate(Labels.MR, storage.getLocale) : translate(Labels.MS, storage.getLocale);

  let isInternationalAddress: boolean;

  if (isWefox) {
    isInternationalAddress = true;
  } else {
    isInternationalAddress = false;
  }

  let zipCodeInt;
  if (isWefox) {
    zipCodeInt = zipCode;
  }

  return {
    initials,
    firstName,
    middleName,
    surname,
    nationality,
    socialSecurityNumber,
    phonePrefix,
    phoneNumberBody,
    email,
    gender,
    birthDate,
    street,
    city,
    zipCode,
    zipCodeInt,
    country,
    number,
    suffix,
    greeting,
    type,
    occupation,
    isSmoker,
    addressType,
    gpName,
    gpCity,
    isInternationalAddress,
  };
};

export const padZero = (num: string, size: number): string => {
  let s = `${num}`;
  while (s.length < size) {
    s = `0${s}`;
  }
  return s;
};

export const MagnumLangConverter: { [key: string]: Language } = {
  nl_NL: Language.Dutch,
  en_GB: Language.English,
};

export const isIE11 = (): boolean => !!navigator.userAgent.match(/Trident\/7\./g);

export function changeHTMLDocumentLanguage(lang: Language): void {
  const htmlEl = document.querySelector('html');

  if (htmlEl) {
    htmlEl.lang = lang?.toLowerCase();
  }
}

export function changePageTitle(lang: Language): void {
  document.title = translate(Labels.DIGITAL_HEALTH_DECLARATION, lang);
}

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const validateHealthDocumentFile = (
  file: File,
  existingDocs: Array<HealthDocFile>,
): HealthDocFileValidation => {
  let isValid = true;
  let error = '' as Labels;
  const TWO_MB = 2097152;
  const TWENTY_MB = 20971520;
  const maxFileSize = isBrand(Brand.DHMR) ? TWENTY_MB : TWO_MB;
  const validExtensions = [
    /* Either doc and docs worked before or never */
    'doc',
    'docx',
    /*----*/
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    /* File types for doc and docs extensions */
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
  ];

  if (file.size > maxFileSize) {
    isValid = false;
    error = Labels.HEALTH_DOC_SIZE_ERROR;
  }

  if (!validExtensions.includes(file.type)) {
    isValid = false;
    error = Labels.HEALTH_DOC_EXT_ERROR;
  }

  existingDocs.forEach((existingDoc) => {
    if (existingDoc.file.name === file.name) {
      isValid = false;
      error = Labels.HEALTH_DOC_DUPLICATION_ERROR;
    }
  });

  return {
    isValid,
    error,
  };
};

export const randomIntFromInterval = (min: number, max: number): number => (
  Math.floor(Math.random() * (max - min + 1) + min)
);

export const createUploadHealthDocsPostData = (files: Array<HealthDocFile>): FormData => {
  const formData = new FormData();
  const descriptions: Record<string, string>[] = [];
  files.forEach((file) => {
    formData.append('file', file.file, file.file.name);
    descriptions.push({
      fileName: file.file.name,
      description: file.description,
    });
  });
  formData.append('input', JSON.stringify(descriptions));
  return formData;
};

export const logFormValidationError = ({
  // eslint-disable-next-line
  fieldName,
  // eslint-disable-next-line
  error,
}: {
  fieldName: string,
  error: string
}) => {

  // TODO: Add implementation of log function

  /* Sentry.addBreadcrumb({
    category: 'form validation',
    message: `Field: ${fieldName} -> Error: ${error} `,
    level: 'warning',
  }); */
};

export function isProductWithMandatorySSN(productType: ProductType): boolean {
  return [ProductType.TIB, ProductType.MLB, ProductType.MLBZ, ProductType.GG, ProductType.IZ, ProductType.BG].includes(productType);
}

export function sortNationalities(nationalities: Nationality[], sortBy: keyof Nationality): Nationality[] {
  return nationalities.sort((a, b) => {
    return  a[sortBy].toLowerCase().localeCompare(b[sortBy].toLowerCase());
  });
}

export function shiftNLToTheTopOfNationalitiesList(nationalities: Nationality[]) {
  const nlIndex = nationalities.findIndex((el) => el.countryCode === 'NL');
  const nl = nationalities.splice(nlIndex, 1)[0];
  nationalities.unshift(nl);
}
